import * as d3 from 'd3';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { Icon } from '../../../../icon/icon';

const ImageContainer = styled.div`
  width: 100%;
  height: 60vh;
  min-height: 400px;
  overflow: hidden;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;

  svg {
    font-size: 48px;
    color: #fff;
  }
`;

const StyledSvg = styled.svg`
  width: 100%;
  height: 100%;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

export const ImageInspectionDialog = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [imageName, setImageName] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const imageLayerRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);

  useSubscribe(SubscriptionTopic.OpenImageInspectionDialog, (message) => {
    setVisible(true);
    setImageUrl(message.url);
    setImageName(message.name);
    setIsLoading(true);
  });

  useEffect(() => {
    if (!visible || !imageUrl || !svgRef.current) {
      return;
    }

    const svg = svgRef.current;
    const imgElement = new Image();

    imgElement.onload = () => {
      componentUtils.setupSvgWithImage(
        svg,
        imageUrl,
        imgElement.naturalWidth,
        imgElement.naturalHeight,
        imageLayerRef,
      );
      setIsLoading(false);
    };

    imgElement.onerror = () => {
      setIsLoading(false);
    };

    imgElement.src = imageUrl;

    return () => {
      if (svg) {
        componentUtils.cleanupSvg(svg, imageLayerRef);
      }
    };
  }, [visible, imageUrl]);

  if (!visible) {
    return null;
  }

  const handleClose = () => {
    setVisible(false);
    setImageUrl(undefined);
    setIsLoading(false);
  };

  return (
    <Dialog maxHeight="expand" width={800} onClose={handleClose}>
      {{
        header: imageName,
        content: (
          <ImageContainer>
            {isLoading && (
              <LoadingSpinner>
                <Icon icon="spinner" spin={true} />
              </LoadingSpinner>
            )}
            <StyledSvg ref={svgRef} />
          </ImageContainer>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isLoading || !imageUrl || !imageName}
                variant="contained"
                onClick={() => {
                  if (imageUrl && imageName) {
                    void componentUtils.downloadImage(imageUrl, imageName);
                  }
                }}
              >
                {t('download', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={handleClose}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export const componentUtils = {
  setupSvgWithImage: _setupSvgWithImage,
  cleanupSvg: _cleanupSvg,
  downloadImage: _downloadImage,
};

function _setupSvgWithImage(
  svgElement: SVGSVGElement,
  imageUrl: string,
  imgWidth: number,
  imgHeight: number,
  imageLayerRef: React.MutableRefObject<d3.Selection<SVGGElement, unknown, null, undefined> | null>,
) {
  const svg = d3.select<SVGSVGElement, unknown>(svgElement);

  // Clear previous content
  svg.selectAll('*').remove();

  // Set SVG viewBox to match image dimensions
  svg
    .attr('viewBox', `0 0 ${imgWidth} ${imgHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('display', 'block');

  // Create image layer group
  const imageLayer = svg.append('g');
  imageLayerRef.current = imageLayer;

  // Add image to the layer
  imageLayer
    .append('image')
    .attr('href', imageUrl)
    .attr('width', imgWidth)
    .attr('height', imgHeight)
    .attr('x', 0)
    .attr('y', 0);

  // Set up zoom behavior
  const zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.5, 10000])
    .wheelDelta(
      (event) =>
        -event.deltaY *
        (event.deltaMode === 1 ? 0.1 : event.deltaMode ? 2 : 0.004) *
        (event.ctrlKey ? 10 : 1),
    )
    .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
      const transform = event.transform;
      imageLayer.attr('transform', transform.toString());
    });

  svg.call(zoom);

  // Initialize zoom to identity (image fits container by default due to viewBox)
  const initialTransform = d3.zoomIdentity;
  svg.call(() => zoom.transform(svg, initialTransform));
}

function _cleanupSvg(
  svgElement: SVGSVGElement,
  imageLayerRef: React.MutableRefObject<d3.Selection<SVGGElement, unknown, null, undefined> | null>,
) {
  const svg = d3.select<SVGSVGElement, unknown>(svgElement);
  svg.selectAll('*').remove();
  imageLayerRef.current = null;
}

function _downloadImage(imageUrl: string, imageName: string) {
  const a = document.createElement('a');
  a.href = imageUrl;
  a.download = imageName;
  a.click();
}
